import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, LoaderDots } from 'components';
import { STATE_ABVS } from 'config/const';
import { theme, toRem } from 'design';
import { ImportScript } from 'hooks';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCardSession } from 'store/App';
import { Card, createTokenizedCardRequest } from 'store/Card';
import { IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import { CardType, IApiErrorPayloadWithCode, ITokenizeCardRequest } from 'types';
import { isProduction } from 'utils';

interface IProps {
  className?: string;
  onScriptLoaded?: () => void;
  onError?: (error: IApiErrorPayloadWithCode) => void;
}

interface IVGSCardField {
  errorMessages: string[];
  isDirty: boolean;
  isEmpty: boolean;
  isFocused: boolean;
  isTouched: boolean;
  isValid: boolean;
  name: string;
}

interface IVGSCardNumberField extends IVGSCardField {
  bin: string;
  cardType: string;
  last4: string;
}

interface IVGSCardState {
  address_1: IVGSCardField;
  address_2: IVGSCardField;
  city: IVGSCardField;
  cvv: IVGSCardField;
  exp_date: IVGSCardField;
  first_name: IVGSCardField;
  last_name: IVGSCardField;
  card_number: IVGSCardNumberField;
  state: IVGSCardField;
  zip_code: IVGSCardField;
}

interface IVGSCardResponse {
  address_1: string;
  address_2: string;
  city: string;
  cvv: string;
  exp_date: string;
  first_name: string;
  last_name: string;
  card_number: string;
  state: string;
  zip_code: string;
}

const SCRIPT_ID = 'vgs-collect-script';
const SCRIPT_URL = 'https://js.verygoodvault.com/vgs-collect/2.15.0/vgs-collect.js';

const VGSCollectPaymentMethodFormLayout = styled.form``;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  &:nth-child(2) {
    margin-left: 16px;
  }

  label {
    margin-bottom: 8px;
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
  }

  .form-field {
    display: block;
    width: 100%;
    height: ${theme.input.height.large}px;
    padding: 0.375rem 0.75rem;
    font-size: ${toRem(14)};
    line-height: 1.5;
    color: #495057;
    background-color: ${theme.color.white};
    background-clip: padding-box;
    border: 1px solid ${theme.color.neutral[300]};
    border-radius: ${theme.borderRadius.xsmall}px;
    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;

    iframe {
      width: 100%;
      height: 100%;
    }
  }
`;

const FormFieldError = styled.p`
  margin-top: 8px;
  font-size: ${toRem(11)};
  color: ${theme.color.danger.medium};

  &:first-letter {
    text-transform: uppercase;
  }
`;

const FormFieldRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 16px;
`;

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  padding: 24px;
  width: 100%;
  border-radius: ${theme.borderRadius.small}px;
  background: ${theme.color.neutral[100]};
`;

const PaymentMethodFormCta = styled(Button)`
  width: 200px;
`;

const FormError = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${toRem(24)};
  padding: ${toRem(24)};
  border-radius: ${theme.borderRadius.small}px;
  background: ${theme.color.danger.light};
  margin-top: 8px;
  font-size: ${toRem(11)};
  color: ${theme.color.danger.medium};

  p {
    font-size: ${toRem(13.25)};
    color: ${theme.color.neutral[900]};
    text-align: center;
    line-height: 1.3;

    &:first-child {
      font-weight: ${theme.font.weight.semiBold};
    }

    &:last-child {
      font-weight: ${theme.font.weight.regular};
    }
  }
`;

const RequiredStar = styled.span`
  margin-left: ${toRem(4)};
`;

const initFields: { [key: string]: any } = {
  address_1: null,
  address_2: null,
  city: null,
  cvv: null,
  exp_date: null,
  first_name: null,
  last_name: null,
  card_number: null,
  state: null,
  zip_code: null,
};

const getNetwork = (cardNumber: string) => {
  switch (cardNumber.substring(0, 1)) {
    case '3':
      return CardType.AMERICAN_EXPRESS.toString().toLowerCase();
    case '4':
      return CardType.VISA.toString().toLowerCase();
    case '5':
      return CardType.MASTER_CARD.toString().toLowerCase();
    case '6':
      return CardType.DISCOVER.toString().toLowerCase();
    default:
      return CardType.OTHER.toString().toLowerCase();
  }
};

export const VGSCollectPaymentMethodForm: React.FC<IProps> = ({ className = '', onScriptLoaded, onError }) => {
  const dispatch = useDispatch();

  const { id: userId } = useSelector(selectCurrentUser());
  const cardSession = useSelector(selectCardSession());
  const sessionId = cardSession?.sessionId;
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Card.CREATE_TOKENIZED_CARD_REQUEST)) as IApiErrorPayloadWithCode;

  const addCardLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Card.CREATE_TOKENIZED_CARD_REQUEST));
  const [form, setForm] = useState<any>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [vgsState, setVGSState] = useState<IVGSCardState>(null);
  const [fields, setFields] = useState<{ [key: string]: any }>(initFields);
  const [isProcessing, setIsProcessing] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const states = useMemo(() => STATE_ABVS.map((state) => ({ value: state, text: state })), []);
  const disableCta = useMemo(() => {
    if (!form || !scriptLoaded || !vgsState || addCardLoading) return true;

    return !!Object.values(vgsState).some((elem) => elem.isValid === false);

    return !!Object.values(vgsState).filter((field) => field.isTouched && !!field.errorMessages.length).length;
  }, [form, scriptLoaded, vgsState, addCardLoading]);

  useEffect(() => {
    const script = document.getElementById(SCRIPT_ID);
    if (script) script.remove();

    ImportScript({
      async: true,
      id: SCRIPT_ID,
      resourceUrl: SCRIPT_URL,
      onload: () => {
        setScriptLoaded(true);
        onScriptLoaded?.();
      },
    });
  }, []);

  useEffect(() => {
    if (!apiErrors?.errorMessages?.length) return;

    switch (apiErrors.statusCode) {
      case '422':
        setSubmissionError(apiErrors.errorMessages[0]);
        break;
      default:
        if (onError) {
          onError(apiErrors);
        } else {
          setSubmissionError('Please try again.');
        }
        break;
    }

    setIsProcessing(false);
  }, [apiErrors?.errorMessages]);

  useEffect(() => {
    if (fields.first_name) fields.first_name.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.last_name) fields.last_name.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.address_1) fields.address_1.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.address_2) fields.address_2.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.city) fields.city.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.state) fields.state.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.zip_code) fields.zip_code.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.cvv) fields.cvv.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.exp_date) fields.exp_date.update({ readOnly: addCardLoading ? 'readonly' : null });
    if (fields.card_number) fields.card_number.update({ readOnly: addCardLoading ? 'readonly' : null });
  }, [fields, addCardLoading]);

  useEffect(() => {
    if (!scriptLoaded) return;

    // eslint-disable-next-line no-console
    const vgsForm = (window as any).VGSCollect.create(process.env.REACT_APP_VGS_VAULT_ID, process.env.REACT_APP_VGS_ENV, (state: IVGSCardState) => setVGSState(state));

    if (!vgsForm) return;

    setForm(vgsForm);

    const updatedFields = { ...fields };

    updatedFields.first_name = vgsForm.field('#cc-first-name', {
      type: 'text',
      name: 'first_name',
      validations: ['required'],
      tokenization: false,
      readOnly: null,
      maxLength: 75,
      autoComplete: 'given-name',
    });

    updatedFields.last_name = vgsForm.field('#cc-last-name', {
      type: 'text',
      name: 'last_name',
      validations: ['required'],
      tokenization: false,
      readOnly: null,
      maxLength: 75,
      autoComplete: 'family-name',
    });

    updatedFields.card_number = vgsForm.field('#cc-number', {
      type: 'card-number',
      name: 'card_number',
      showCardIcon: true,
      validations: ['required', 'validCardNumber'],
      tokenization: { format: 'FPE_SIX_T_FOUR', storage: 'PERSISTENT' },
      readOnly: null,
      autoComplete: 'cc-number',
      ...(!isProduction() && {
        addCardBrands: [
          {
            type: 'other',
            pattern: /^9/,
            format: /(\d{1,4})/g,
            length: [16],
            cvcLength: [3],
            luhn: true,
          },
        ],
      }),
    });

    updatedFields.exp_date = vgsForm.field('#cc-expiration-date', {
      type: 'card-expiration-date',
      name: 'exp_date',
      validations: ['required', 'validCardExpirationDate'],
      tokenization: false,
      readOnly: null,
      yearLength: '2',
      autoComplete: 'cc-exp',
    });

    updatedFields.cvv = vgsForm.field('#cc-cvv', {
      type: 'card-security-code',
      name: 'cvv',
      maxLength: 4,
      validations: ['required', 'validCardSecurityCode'],
      tokenization: { format: 'NUM_LENGTH_PRESERVING', storage: 'VOLATILE' },
      readOnly: null,
      autoComplete: 'cc-csc',
    });

    updatedFields.address_1 = vgsForm.field('#cc-address-1', {
      type: 'text',
      name: 'address_1',
      validations: ['required'],
      tokenization: false,
      readOnly: null,
      maxLength: 250,
      autoComplete: 'street-address',
    });

    updatedFields.address_2 = vgsForm.field('#cc-address-2', {
      type: 'text',
      name: 'address_2',
      validations: [],
      tokenization: false,
      readOnly: null,
      maxLength: 50,
      autoComplete: 'address-line2',
    });

    updatedFields.city = vgsForm.field('#cc-city', {
      type: 'text',
      name: 'city',
      validations: ['required'],
      tokenization: false,
      readOnly: null,
      maxLength: 100,
      autoComplete: 'address-level2',
    });

    updatedFields.state = vgsForm.field('#cc-state', {
      type: 'dropdown',
      name: 'state',
      validations: ['required'],
      options: states,
      tokenization: false,
      readOnly: null,
      autoComplete: 'address-level1',
    });

    updatedFields.zip_code = vgsForm.field('#cc-zip', {
      type: 'zip-code',
      name: 'zip_code',
      validations: ['required', 'postal_code/us'],
      tokenization: false,
      readOnly: null,
      autoComplete: 'postal-code',
    });

    setFields(updatedFields);
  }, [scriptLoaded]);

  const onVGSError = useCallback(
    (error: IVGSCardState) => {
      setVGSState({
        ...vgsState,
        ...error,
      });

      setIsProcessing(false);
    },
    [vgsState]
  );

  const onVGSSuccess = useCallback(
    (_: any, { cvv, exp_date, first_name, last_name, address_1, address_2, city, state, zip_code, card_number }: IVGSCardResponse) => {
      const cardRequest: ITokenizeCardRequest = {
        cvv,
        expDate: exp_date.split(' ').join(''),
        last4: card_number.substring(card_number.length - 4),
        network: getNetwork(card_number.substring(0, 1)),
        owner: {
          firstName: first_name,
          lastName: last_name,
          address1: address_1,
          address2: address_2,
          city,
          state,
          zipCode: zip_code,
        },
        cardNumber: card_number,
        userId,
        sessionId,
      };

      dispatch(createTokenizedCardRequest(cardRequest));
    },
    [userId, sessionId]
  );

  // build a friendly error message
  const buildErrorMessage = (fieldState: IVGSCardField) => {
    let fieldName = fieldState.name.replaceAll('_', ' ');
    // Set name
    switch (fieldState.name) {
      case 'exp_date': {
        fieldName = 'expiration';
        break;
      }
      case 'address_1': {
        fieldName = 'billing address';
        break;
      }
      case 'zip': {
        fieldName = 'zip code';
        break;
      }
      case 'cvv': {
        fieldName = 'CVV';
        break;
      }
      default: {
        break;
      }
    }

    // Set error
    if (fieldState.errorMessages[0] === 'is required') {
      return `${fieldName} ${fieldState.errorMessages[0]}`;
    }
    return fieldState.errorMessages[0];
  };

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!form) return;

      setIsProcessing(true);

      form.tokenize(onVGSSuccess, onVGSError);
    },
    [form, onVGSSuccess, onVGSError]
  );

  const renderError = (fieldState: IVGSCardField) => {
    if (!fieldState?.isTouched || !fieldState?.errorMessages?.length || fieldState.isFocused) return null;

    return (
      <FormFieldError className={fieldState.name} data-cy={fieldState.name}>
        {buildErrorMessage(fieldState)}
      </FormFieldError>
    );
  };

  const renderSubmissionError = () => {
    if (!submissionError) return null;

    return (
      <FormError className="form-error">
        <p>Something went wrong!</p>
        <p>{submissionError}</p>
      </FormError>
    );
  };

  if (!scriptLoaded) return <LoaderDots />;

  return (
    <VGSCollectPaymentMethodFormLayout className={className} onSubmit={handleSubmit}>
      <FormFieldsWrapper>
        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-first-name">
              First Name<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-first-name" className="form-field" />
            {renderError(vgsState?.first_name)}
          </FormField>
          <FormField>
            <label htmlFor="cc-last-name">
              Last Name<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-last-name" className="form-field" />
            {renderError(vgsState?.last_name)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-number">
              Card Number<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-number" className="form-field" />
            {renderError(vgsState?.card_number)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-expiration-date">
              Expiration<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-expiration-date" className="form-field" />
            {renderError(vgsState?.exp_date)}
          </FormField>
          <FormField>
            <label htmlFor="cc-cvv">
              CVV<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-cvv" className="form-field" />
            {renderError(vgsState?.cvv)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-address-1">
              Billing Address<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-address-1" className="form-field" />
            {renderError(vgsState?.address_1)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-address-2">Unit / Apt #</label>
            <div id="cc-address-2" className="form-field" />
            {renderError(vgsState?.address_2)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-city">
              City<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-city" className="form-field" />
            {renderError(vgsState?.city)}
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField>
            <label htmlFor="cc-state">
              State<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-state" className="form-field" />
            {renderError(vgsState?.state)}
          </FormField>
          <FormField>
            <label htmlFor="cc-zip">
              Zip<RequiredStar>*</RequiredStar>
            </label>
            <div id="cc-zip" className="form-field" />
            {renderError(vgsState?.zip_code)}
          </FormField>
        </FormFieldRow>
      </FormFieldsWrapper>

      {renderSubmissionError()}

      <PaymentMethodFormCta appearance="primary" label="Pay To This Card" data-cy="add-payment-method-submit-button" disabled={disableCta} isProcessing={addCardLoading || isProcessing} type="submit" />
    </VGSCollectPaymentMethodFormLayout>
  );
};
